import React, { useState } from "react";
import { Box, Paper, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import {
  HtmlTooltip,
  IconContainer,
  StyledDarkRedInformationTypography,
  StyledStack,
} from "../../../components/commonComponents/StyledComponents/styled";

import { CustomTextInput } from "../../../components/commonComponents/TextInputs/style";
import { ListingCellCenter, ListingCellLeft, ListingCellRight, ListingIconMetric, StyledTableCell } from "../../../components/commonComponents/TabularLists/styles";
import { ArrowDropDown, ArrowDropUp, Download } from "@mui/icons-material";
import {
  downloadCyreenFile,
  sortCyreenFilesList,
  viewCyreenUploads,
} from "../../../api_calls/dataExchange";
import { formatDate, SortIcon } from "../../../api_calls/utils";
import ListingDataTableForModal from "../../../components/commonComponents/TabularLists/ListingDataTableForModal";
import ListingDataTable from "../../../components/commonComponents/TabularLists/ListingDataTable";

const DownloadData = () => {
  const userStateData = useSelector((state) => state.userSelections);
  const [searchVal, setSearchVal] = useState({ value: "" });
  const [sortingMeasure, setSortingMeasure] = React.useState({
    fileName: "asc",
    uploadDate: "asc",
    fileSize: "asc",
  });
  const sortUploadsList = (sortingParamName, sortingParamValue) => {
    sortCyreenFilesList(sortingParamName, sortingParamValue);
  };

  const cyreenFilesList =
    typeof userStateData.cyreen_file_upload_list !== "undefined"
      ? typeof searchVal.value !== "undefined"
        ? userStateData.cyreen_file_upload_list.filter((fileContent) => fileContent.name.includes(searchVal.value))
        : userStateData.cyreen_file_upload_list
      : [];

  const columns = React.useMemo(() => {
    let cols = [
      {
        id: "select",
        headerAlign: "left",
        align: "left",
        header: (
          <ListingCellLeft>
            #
          </ListingCellLeft>
        ),
        render: (fileDetail, index) => (
          <ListingCellLeft>
            {index + 1}
          </ListingCellLeft>
        ),
      },
      {
        id: "filename",
        headerAlign: "left",
        align: "left",
        header: (
          <ListingCellLeft
            onClick={() => {
              setSortingMeasure((prevState) => {
                return {
                  ...prevState,
                  fileName: prevState.fileName === "asc" ? "desc" : "asc",
                };
              });
              // sort the listing in redux state
              sortUploadsList("fileName", sortingMeasure.fileName === "asc" ? "desc" : "asc");
            }} style={{ cursor: "pointer" }}
          >
            File Name  <SortIcon direction={sortingMeasure.fileName} />
          </ListingCellLeft>
        ),
        render: (fileDetail, index) => (
          <ListingCellLeft>
            {fileDetail.name}
          </ListingCellLeft>
        ),
      },
      {
        id: "uploadDate",
        headerAlign: "left",
        align: "left",
        header: (
          <ListingCellLeft
            onClick={() => {
              setSortingMeasure((prevState) => {
                return {
                  ...prevState,
                  uploadDate: prevState.uploadDate === "asc" ? "desc" : "asc",
                };
              });
              // sort the listing in redux state
              sortUploadsList("uploadDate", sortingMeasure.uploadDate === "asc" ? "desc" : "asc");
            }} style={{ cursor: "pointer" }}
          >
            Upload Date  <SortIcon direction={sortingMeasure.uploadDate} />
          </ListingCellLeft>
        ),
        render: (fileDetail, index) => (
          <ListingCellLeft>
            {formatDate(fileDetail.lastModified)}
          </ListingCellLeft>
        ),
      },
      {
        id: "fileSize",
        headerAlign: "left",
        align: "left",
        header: (
          <ListingCellLeft
            onClick={() => {
              setSortingMeasure((prevState) => {
                return {
                  ...prevState,
                  fileSize: prevState.fileSize === "asc" ? "desc" : "asc",
                };
              });
              // sort the listing in redux state
              sortUploadsList("fileSize", sortingMeasure.fileSize === "asc" ? "desc" : "asc");
            }} style={{ cursor: "pointer" }}
          >
            File Size  <SortIcon direction={sortingMeasure.fileSize} />
          </ListingCellLeft>
        ),
        render: (fileDetail, index) => (
          <ListingCellLeft>
            {fileDetail.size}
          </ListingCellLeft>
        ),
      },
      {
        id: "select",
        headerAlign: "center",
        align: "center",
        header: (
          <ListingCellCenter>
            Action
          </ListingCellCenter>
        ),
        render: (fileDetail, index) => (
          <ListingCellCenter>
            <HtmlTooltip title={"Download"}>
              <a
                href="#downloadFile"
                onClick={(e) => {
                  e.preventDefault();
                  downloadCyreenFile(fileDetail.name);
                }}
              >
                <Download sx={{ color: "#AF3241", marginTop: "5px" }} />
              </a>
            </HtmlTooltip>
          </ListingCellCenter>
        ),
      }
    ]
    return cols;
  }, [searchVal.value, userStateData.cyreen_file_upload_list])

  React.useEffect(() => {
    viewCyreenUploads();
  }, []);

  return (
    <>
      <Typography variant="body2" sx={{ fontSize: "15px" }}>
        In this section you can download files provided by Cyreen.
      </Typography>

      <Box height={10} />
      {/* ========== search box and receipt monitoring link ========== */}
      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
        <Box width={300}>
          <CustomTextInput
            variant="standard"
            fullWidth
            label="Search"
            placeholder="Search file name"
            type={"text"}
            autoComplete="off"
            value={searchVal.value}
            onChange={(e) => {
              setSearchVal({ value: e.target.value });
            }}
            onBlur={(e) => { }}
          />
        </Box>
        <Box display="flex" alignItems="center">
          <Box ml={2}>
            {/* Add margin between the buttons */}
            {/* <ActionButton
              onClick={(e) => {
                e.preventDefault();
              }}
              label="Receipt Monitoring"
              icon={<></>}
            /> */}
          </Box>
        </Box>
      </Box>
      <Box height={20} />
      <ListingDataTable
        columns={columns}
        rows={cyreenFilesList}
        rowKey={(fileDetail) => fileDetail.id}
        emptyText="No files available yet."
        loading={typeof userStateData.cyreen_file_upload_list === "undefined" ? true : false}
      />
      {/* --------- record count info --------- */}
      <Box mt={2} id="infoText">
        <StyledDarkRedInformationTypography variant="body2">
          Showing {cyreenFilesList.length} out of {cyreenFilesList.length} results.
        </StyledDarkRedInformationTypography>
      </Box>
    </>
  );
};

export default DownloadData;
